import * as React from 'react';
import { Grid, TextField, Autocomplete, Box } from '@mui/material';
import { useWatch, UseControllerProps, Controller, FieldValues } from 'react-hook-form';

import _throttle from 'lodash/throttle';
import _startCase from 'lodash/startCase';

import useFindEmployee from '../core/api/hooks/common/useFindEmployee';

import { Employee, EmployeePosition } from '../core/api/clients';

import ReadOnlyGroup from './ReadOnlyGroup';

import { useFormUpdateProxy } from '../core/context/FormUpdateProxyContext';

import EditableGroup from './EditableGroup';
import useEditableControllers from '../core/api/hooks/common/useEditableControllers';

interface IControlledPeoplePicker<TFieldValues extends FieldValues> extends UseControllerProps<TFieldValues> {
  groupName: string,
  fieldNames: string[]
  label?: string,
  position: EmployeePosition,
  readOnly?: boolean,
  onPersonSelected: (employee: Employee | null) => void,
  disableEditPopper?: boolean
}

function ControlledPeoplePicker<TFieldValues extends FieldValues>({
  fieldNames,
  groupName,
  name,
  label,
  position,
  readOnly,
  control,
  rules,
  onPersonSelected,
  disableEditPopper,
}: IControlledPeoplePicker<TFieldValues>): JSX.Element {
  const { requestUpdate } = useFormUpdateProxy();
  const { mutate: findEmployee } = useFindEmployee();

  const [options, setOptions] = React.useState<Employee[]>([]);
  const [value, setValue] = React.useState<Employee | null>(null);
  const [inputValue, setInputValue] = React.useState<string>('');
  const { watchedValues, revert, commit } = useEditableControllers({
    fieldNames,
    control,
  });

  const fetchEmployeeOptions = React.useMemo(
    () => _throttle((request: { input: string }, callback: (employees: Employee[]) => void) => {
      findEmployee({ query: request.input, position }, {
        onSuccess: (employees: Employee[]) => callback(employees),
        onError: () => {
          console.log('ERROR'); // TODO: Handle this
        },
      });
    }, 200),
    [],
  );

  React.useEffect(() => {
    setValue({
      id: watchedValues[0],
      name: watchedValues[1],
      location: watchedValues[2],
    });
  }, [watchedValues]);

  React.useEffect(() => {
    let active = true;

    if (inputValue === '') {
      setOptions(value ? [value] : []);
    }

    if (inputValue.length >= 3) {
      fetchEmployeeOptions({ input: inputValue }, (results?: readonly Employee[]) => {
        if (active) {
          let newOptions: Employee[] = [];

          if (value) newOptions = [value];
          if (results) newOptions = [...newOptions, ...results];

          setOptions(newOptions);
        }
      });
    }

    return () => {
      active = false;
    };
  }, [value, inputValue, fetchEmployeeOptions]);
  return (
    <EditableGroup
      fieldNames={fieldNames}
      renderMode={readOnly ? 'read' : 'edit'}
      activeIndex={0}
      saveSuccess={() => {
        commit();
        requestUpdate();
      }}
      onEditCancel={() => {
        revert();
      }}
      disableEditPopper={disableEditPopper}
      renderEditFields={() => (
        <Autocomplete
          autoComplete
          options={options}
          value={value || null}
          filterOptions={(x) => x}
          onChange={(event, newValue: Employee | null) => {
            setOptions(newValue ? [newValue, ...options] : options);
            setValue(newValue);

            onPersonSelected(newValue);
          }}
          onInputChange={(event, newInputValue) => {
            setInputValue(newInputValue);
          }}
          getOptionLabel={(employee: Employee) => {
            if (employee && employee.name && employee.id && employee.location) {
              return `${employee.name} [${employee.id}], ${employee.location}`;
            }

            return '';
          }}
          renderInput={(params) => (
            <Controller
              name={name}
              rules={rules}
              render={({ fieldState: { invalid, error } }) => (
                <TextField
                  {...params}
                  fullWidth
                  error={invalid}
                  variant="outlined"
                  helperText={invalid && error ? error.message : null}
                  label={`${label ?? _startCase(groupName)}${rules?.required === true ? ' *' : ''}`}
                />
              )}
            />
          )}
        />
      )}
      renderReadFields={() => (
        <ReadOnlyGroup
          name={groupName ?? 'crewMember'}
          render={() => (
            <Box>
              {`${value?.name ?? ''} ${value?.id?.toString() ? `[${value.id.toString()}]` : ''} ${value?.location ? `, ${value?.location}` : ''}`}
            </Box>
          )}
        />
      )}
    />
  );
}

export default ControlledPeoplePicker;
